/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Checkbox Component for Radiant React
 */
import cx from 'classnames';
import React, { useCallback, useEffect, useImperativeHandle, useRef, } from 'react';
import { Key } from 'w3c-keys';
import { Icon, IconColor, IconID, IconSize } from '../icon';
import { SafeHTML } from '../safe-html';
import { Typography } from '../typography';
import styles from './checkbox.module.scss';
export const Checkbox = React.forwardRef(({ id, label, checked, onChange, indeterminate = false, disabled = false, hasError = false, hideLabel = false, className = '', checkboxControlClassName = '', isList = false, wrapLabel = false, disabledTooltipContent = '', tooltipContent = '', showTooltipOnEllipsis = false, useSafeHTML = false, safeHTMLConfig, highlightOnHover = false, customLabelClassName = '', ariaLabel = '', ...restProps }, forwardedRef) => {
    /**
     * Note: Indeterminate property of the native input element
     * can only be changed by Javascript and not throught HTML,
     * which is why we need a ref and useEffect here.
     * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
     */
    const inputEl = useRef(null);
    const checkBoxElementRef = useRef(null);
    useImperativeHandle(forwardedRef, () => ({
        getCheckBoxElement: () => checkBoxElementRef.current,
    }));
    const syncIndeterminateState = useCallback(() => {
        if (inputEl && inputEl.current) {
            inputEl.current.indeterminate = indeterminate;
        }
    }, [inputEl, indeterminate]);
    useEffect(() => {
        syncIndeterminateState();
    }, [indeterminate, syncIndeterminateState]);
    const handleChange = (event) => {
        event.stopPropagation();
        /**
         * If checkbox is currently indeterminate, we need to sync
         * the indeterminate state between the DOM node (input) and React.
         * This is required due to browser default behavior which changes
         * the indeterminate property of the checkbox on click.
         *
         * Note: We can't use event.preventDefault() due to this issue:
         * https://github.com/facebook/react/issues/3005
         */
        if (indeterminate) {
            syncIndeterminateState();
        }
        onChange(event, id);
    };
    const containerClass = cx(className, styles.container, { [styles.hasError]: hasError }, { [styles.disabled]: disabled }, { [styles.listCheckbox]: isList }, { [styles.highlightedContainer]: highlightOnHover });
    const labelClass = cx(styles.labelText, {
        [styles.hiddenLabel]: hideLabel,
        [styles.disabled]: disabled,
        [customLabelClassName]: !!customLabelClassName,
    });
    const dataTooltipContent = disabled
        ? disabledTooltipContent
        : tooltipContent;
    // Tooltip config to be used when label is shown
    // Handles both "disabled checkbox" and "text ellipsis" case
    const labelTooltipConfig = React.useMemo(() => {
        return {
            showTooltipOnlyOnEllipsis: !disabled && showTooltipOnEllipsis,
            dataTooltipContent,
            disableTooltip: !dataTooltipContent,
        };
    }, [dataTooltipContent, disabled, showTooltipOnEllipsis]);
    // Tooltip props to be used when label is not shown
    // Handles "disabled checkbox" case
    const checkboxTooltipProps = disabled && disabledTooltipContent && hideLabel
        ? { 'data-tooltip-content': disabledTooltipContent }
        : {};
    const onKeyDown = (event) => {
        if (event.key === Key.Enter) {
            handleChange(event);
        }
    };
    return (React.createElement("div", { id: id, className: containerClass, ...checkboxTooltipProps, ...restProps },
        React.createElement("label", { className: styles.label },
            React.createElement("input", { type: "checkbox", className: styles.nativeCheckbox, disabled: disabled, checked: checked, onChange: handleChange, ref: inputEl, onClick: (event) => event.stopPropagation(), onKeyDown: onKeyDown, "aria-label": disabled ? '' : ariaLabel }),
            React.createElement("span", { ref: checkBoxElementRef, className: cx(styles.customCheckbox, checkboxControlClassName) },
                checked && !indeterminate && (React.createElement(Icon, { color: IconColor.TEXT_COLOR, size: IconSize.MEDIUM, id: IconID.CHECKMARK })),
                indeterminate && (React.createElement(Icon, { color: IconColor.TEXT_COLOR, size: IconSize.MEDIUM, id: IconID.MINUS }))),
            useSafeHTML ? (React.createElement(SafeHTML, { html: label, className: cx(labelClass, styles.htmlLabelClass), tooltipConfig: labelTooltipConfig, config: safeHTMLConfig, hyperLinkSanitization: true })) : (React.createElement(Typography, { variant: "body-normal", className: labelClass, overrideTag: "span", preserveWhitespace: wrapLabel, ellipsis: { rows: 1 }, tooltipConfig: labelTooltipConfig }, label)))));
});
//# sourceMappingURL=checkbox.js.map